import { z } from 'zod';

import { FlightPaxGenderSchema, FlightPaxTitleSchema } from './flight-booking.js';
import { MyBookingProductSchema } from './my-bookings.js';

/**
 * Website "Saved travellers" — the people a signed-in customer has booked for
 * before, served to the flight traveller step and the hotel guest step so a
 * returning customer ticks a name instead of retyping it.
 *
 * Derived, not stored: there is no saved-traveller table. The list is built on
 * demand from `TravelBookingPassenger` / `TravelBookingGuest` rows on the
 * customer's *paid* bookings, newest first, one entry per distinct person.
 * Both products feed one list — a hotel guest is a flight passenger next
 * month — so a person can carry a hotel-shaped subset of the fields (no date
 * of birth, no nationality) and the form fills what it can.
 *
 * Passport numbers travel in full: the endpoint is cookie-authenticated and
 * the reader is the person who typed them. The browser masks them for display
 * and never writes them anywhere that outlives the tab.
 */

/** Calendar date as YYYY-MM-DD. */
const CalendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
  .describe('Calendar date YYYY-MM-DD');

export const SavedTravellerSchema = z.object({
  /**
   * Stable within one response only — a hash of the normalised name and date
   * of birth, so the browser can key a checkbox on it. Not a database id.
   */
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  title: FlightPaxTitleSchema.nullable(),
  gender: FlightPaxGenderSchema.nullable(),
  dateOfBirth: CalendarDateSchema.nullable(),
  /** ISO-3166-1 alpha-2. */
  nationality: z.string().nullable(),
  passportNumber: z.string().nullable(),
  passportExpiry: CalendarDateSchema.nullable(),
  passportIssueDate: CalendarDateSchema.nullable(),
  /** ISO-3166-1 alpha-2. */
  passportIssuingCountry: z.string().nullable(),
  panNumber: z.string().nullable(),
  frequentFlyerAirline: z.string().nullable(),
  frequentFlyerNumber: z.string().nullable(),
  /** Which products this person has appeared on. Never empty. */
  products: z.array(MyBookingProductSchema).min(1),
  /** When the most recent booking naming this person was paid for. */
  lastBookedAt: z.string().datetime(),
});
export type SavedTraveller = z.infer<typeof SavedTravellerSchema>;

/**
 * The contact block from the customer's most recent paid booking, falling back
 * to the account itself. GST is deliberately absent: a company GSTIN on the
 * last trip says nothing about whether this one is being expensed.
 */
export const SavedContactSchema = z.object({
  email: z.string(),
  /** Dial digits without the `+`, e.g. "91". */
  phoneCountryCode: z.string(),
  phone: z.string(),
  addressLine: z.string().nullable(),
  city: z.string().nullable(),
  /** Indian state, as the hotel form collects it for the tax invoice. */
  state: z.string().nullable(),
  /** ISO-3166-1 alpha-2. */
  countryCode: z.string().nullable(),
});
export type SavedContact = z.infer<typeof SavedContactSchema>;

export const SavedTravellersResponseSchema = z.object({
  travellers: z.array(SavedTravellerSchema),
  contact: SavedContactSchema.nullable(),
});
export type SavedTravellersResponse = z.infer<typeof SavedTravellersResponseSchema>;
